// src/helpers/rutaReportes.js

const BASES = [
  // "http://localhost:3000",
  "https://backsanpancracio.us-3.evennode.com",
];

/* -------------------- helpers -------------------- */

const cleanBase = (b) => String(b || "").replace(/\/$/, "");

const toNum = (n) => Number(n || 0);
const toStr = (v) => (v == null ? "" : String(v));
const toDate = (d) => (d ? new Date(d) : null);

function buildHeaders(extra = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { token, Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function fetchJSONWithFallback(pathAndQuery, options = {}) {
  let lastErr = null;

  for (const base of BASES) {
    const url = `${cleanBase(base)}${pathAndQuery}`;
    try {
      const r = await fetch(url, {
        method: "GET",
        ...options,
        headers: buildHeaders(options.headers || {}),
      });

      const json = await r.json().catch(() => ({}));

      if (!r.ok || json?.ok === false) {
        lastErr = {
          ok: false,
          error: json?.msg || json?.error || `HTTP ${r.status}`,
          tried: url,
          status: r.status,
        };
        continue;
      }

      return { ok: true, json, tried: url };
    } catch (e) {
      lastErr = { ok: false, error: e?.message || String(e), tried: url };
    }
  }

  return lastErr || { ok: false, error: "No se pudo conectar.", tried: "" };
}

/* -------------------- API -------------------- */

export function toISODate(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

export async function getSaldoCliente(clienteId, fechaISO) {
  if (!clienteId) return { ok: false, error: "clienteId requerido" };

  const q = fechaISO ? `?fecha=${encodeURIComponent(fechaISO)}` : "";
  const r = await fetchJSONWithFallback(`/reportes/saldo-cliente/${clienteId}${q}`);

  if (!r?.ok) {
    console.error("[getSaldoCliente] fallo:", r);
    return { ok: false, error: "No se pudo conectar con el servidor de reportes.", tried: r?.tried };
  }

  const json = r.json;

  return {
    ok: true,
    clienteId: toStr(json.clienteId),
    fechaCorte: toDate(json.fechaCorte),
    totalSaldoComandas: toNum(json.totalSaldoComandas),

    comandasConSaldo: (json.comandasConSaldo || []).map((i) => ({
      ...i,
      numero: toStr(i.numero ?? i.codcom ?? i.nrodecomanda ?? i.num ?? ""),
      nroviaje: toStr(
        i.nroviaje ??
          i.nroViaje ??
          i.nro_viaje ??
          i.nro ??
          i.nrovia ??
          i.nro_via ??
          i.nroVia ??
          ""
      ),
      fecha: toDate(i.fecha),
      total: toNum(i.total),
      aplicado: toNum(i.aplicado),
      saldo: toNum(i.saldo),
    })),

    ordenesCobroAbiertas: (json.ordenesCobroAbiertas || []).map((i) => ({
      ...i,
      total: toNum(i.total),
      fecha: toDate(i.fecha),
    })),
  };
}

export async function getSaldosRemitos(fechaISO) {
  const fecha = fechaISO || new Date().toISOString().slice(0, 10);
  const q = `?fecha=${encodeURIComponent(fecha)}`;

  const r = await fetchJSONWithFallback(`/reportes/saldos-remitos${q}`);

  if (!r?.ok) {
    console.error("[getSaldosRemitos] fallo:", r);
    return { ok: false, error: "No se pudo conectar con el servidor de reportes.", tried: r?.tried };
  }

  const json = r.json;

  return {
    ok: true,
    fechaCorte: toStr(json.fechaCorte || fecha),

    clientes: (json.clientes || []).map((x) => ({
      id: toStr(x.id || x._id || ""),
      razonsocial: toStr(x.razonsocial),
      saldo: toNum(x.saldo),
      saldoA: toNum(x.saldoA),
      saldoB: toNum(x.saldoB),
      saldoFlete: toNum(x.saldoFlete),
    })),

    proveedores: (json.proveedores || []).map((x) => ({
      id: toStr(x.id || x._id || ""),
      razonsocial: toStr(x.razonsocial),
      saldo: toNum(x.saldo),
      saldoA: toNum(x.saldoA),
      saldoB: toNum(x.saldoB),
      saldoFlete: toNum(x.saldoFlete),
    })),

    totales: {
      clientes: toNum(json?.totales?.clientes),
      proveedores: toNum(json?.totales?.proveedores),
      neto: toNum(json?.totales?.neto),
    },
  };
}

export async function getCtaCteEntidad({ tipo, id, desde, hasta }) {
  if (!tipo) return { ok: false, error: "tipo requerido (CLIENTE|PROVEEDOR)" };
  if (!id) return { ok: false, error: "id requerido" };
  if (!desde || !hasta) return { ok: false, error: "desde/hasta requeridos" };

  const q =
    `?tipo=${encodeURIComponent(tipo)}` +
    `&id=${encodeURIComponent(id)}` +
    `&desde=${encodeURIComponent(desde)}` +
    `&hasta=${encodeURIComponent(hasta)}`;

  const r = await fetchJSONWithFallback(`/reportes/ctacte${q}`);

  if (!r?.ok) {
    console.error("[getCtaCteEntidad] fallo:", r);
    return {
      ok: false,
      error: r?.error || "No se pudo conectar con el servidor de reportes.",
      tried: r?.tried,
    };
  }

  const json = r.json;

  return {
    ok: true,

    // NUEVO: dejamos pasar el análisis que viene del backend
    versionReporteCtaCte: toStr(json?.versionReporteCtaCte),
    analisisRemitos: json?.analisisRemitos || null,
    
    tipo: toStr(json.tipo),
    desde: toStr(json.desde),
    hasta: toStr(json.hasta),

    entidad: {
      id: toStr(json?.entidad?.id || id),
      razonsocial: toStr(json?.entidad?.razonsocial || json?.entidad?.nombre || ""),
    },

    saldoInicial: {
      saldoA: toNum(json?.saldoInicial?.saldoA),
      saldoB: toNum(json?.saldoInicial?.saldoB),
      saldoFlete: toNum(json?.saldoInicial?.saldoFlete),
      saldo: toNum(json?.saldoInicial?.saldo),
    },

    totalesPeriodo: {
      debeA: toNum(json?.totalesPeriodo?.debeA),
      debeB: toNum(json?.totalesPeriodo?.debeB),
      debeFlete: toNum(json?.totalesPeriodo?.debeFlete),
      haberA: toNum(json?.totalesPeriodo?.haberA),
      haberB: toNum(json?.totalesPeriodo?.haberB),
      haberFlete: toNum(json?.totalesPeriodo?.haberFlete),
      debe: toNum(json?.totalesPeriodo?.debe),
      haber: toNum(json?.totalesPeriodo?.haber),
    },

    saldoFinal: {
      saldoA: toNum(json?.saldoFinal?.saldoA),
      saldoB: toNum(json?.saldoFinal?.saldoB),
      saldoFlete: toNum(json?.saldoFinal?.saldoFlete),
      saldo: toNum(json?.saldoFinal?.saldo),
    },

    movimientos: (json?.movimientos || []).map((m) => ({
      kind: toStr(m.kind),
      fecha: toDate(m.fecha),
      fechaYMD: toStr(m?.fecha ? String(m.fecha).slice(0, 10) : ""),

      comprobante: toStr(m.comprobante),
      concepto: toStr(m.concepto),

      // extras (si el back los manda)
      nroFac: toStr(m.nroFac),
      choferNombre: toStr(m.choferNombre),
      kgRecibido: toNum(m.kgRecibido),
      precio: toNum(m.precio),

      debeA: toNum(m.debeA),
      debeB: toNum(m.debeB),
      debeFlete: toNum(m.debeFlete),
      haberA: toNum(m.haberA),
      haberB: toNum(m.haberB),
      haberFlete: toNum(m.haberFlete),

      debe: toNum(m.debe),
      haber: toNum(m.haber),

      saldoA: toNum(m.saldoA),
      saldoB: toNum(m.saldoB),
      saldoFlete: toNum(m.saldoFlete),
      saldo: toNum(m.saldo),

      refId: toStr(m.refId),
    })),
  };
}

export async function getCtaCte({ tipo, id, desde, hasta }) {
  if (!tipo || !id || !desde || !hasta) {
    return { ok: false, error: "tipo, id, desde y hasta son requeridos" };
  }

  const q =
    `?tipo=${encodeURIComponent(tipo)}` +
    `&id=${encodeURIComponent(id)}` +
    `&desde=${encodeURIComponent(desde)}` +
    `&hasta=${encodeURIComponent(hasta)}`;

  const r = await fetchJSONWithFallback(`/reportes/ctacte${q}`);

  if (!r?.ok) {
    console.error("[getCtaCte] fallo:", r);
    return { ok: false, error: "No se pudo conectar con el servidor de reportes.", tried: r?.tried };
  }

  return r.json;
}

export async function regularizarCtaCteEntidad({
  tipo,
  id,
  observaciones = "",
}) {
  if (!tipo) {
    return {
      ok: false,
      error: "tipo requerido (CLIENTE|PROVEEDOR)",
    };
  }

  if (!id) {
    return {
      ok: false,
      error: "id requerido",
    };
  }

  const r = await fetchJSONWithFallback(
    "/reportes/ctacte/regularizar",
    {
      method: "POST",

      body: JSON.stringify({
        tipo,
        id,
        observaciones,
      }),
    }
  );

  if (!r?.ok) {
    console.error(
      "[regularizarCtaCteEntidad] fallo:",
      r
    );

    return {
      ok: false,

      error:
        r?.error ||
        "No se pudo realizar la regularización de saldos.",

      tried: r?.tried,
    };
  }

  const json = r.json || {};

  return {
    ok: true,

    tipo: toStr(json.tipo),

    aplicadas: toNum(
      json.aplicadas
    ),

    totalRegularizado: toNum(
      json.totalRegularizado
    ),

    compensaciones: Array.isArray(
      json.compensaciones
    )
      ? json.compensaciones.map(
          (c) => ({
            origen: toStr(
              c.origen
            ),

            destino: toStr(
              c.destino
            ),

            aplicadoA: toNum(
              c.aplicadoA
            ),

            aplicadoB: toNum(
              c.aplicadoB
            ),

            aplicadoFlete: toNum(
              c.aplicadoFlete
            ),

            importe: toNum(
              c.importe
            ),
          })
        )
      : [],

    msg: toStr(
      json.msg ||
        "Regularización aplicada correctamente."
    ),
  };
}